import { z } from "zod";
import { defineEndpoint, idParamsSchema } from "./endpoint.js";
/**
 * A product in the online store (B-74, docs/specs/storefront.md "Catalog", SF-04): published or not, its address
 * (slug) and search-engine texts. Kept apart from the product itself so the catalog contract stays as it is.
 */
const tag = { tag: "storefront" };
const manage = { permission: "storefront.manage" };
/** URL path segment of a product: lower-case letters (any script, Bengali too), digits and dashes. */
export const productSlugSchema = z
    .string()
    .trim()
    .min(1)
    .max(80)
    .regex(/^[\p{Ll}\p{Lo}\p{M}\p{Nd}]+(-[\p{Ll}\p{Lo}\p{M}\p{Nd}]+)*$/u);
/** ST-07 (A4): a product's specifications as titled groups of label / value rows (shown as a table or tabs). */
export const productSpecsSchema = z
    .array(z.object({
    title: z.string().trim().max(60),
    rows: z
        .array(z.object({ label: z.string().trim().min(1).max(60), value: z.string().trim().min(1).max(200) }))
        .min(1)
        .max(30),
}))
    .max(8);
/** ST-07 (A4): a size chart — column headings and rows of cells (same length), its unit and how to measure. */
export const sizeGuideSchema = z
    .object({
    unit: z.enum(["inch", "cm", "none"]),
    columns: z.array(z.string().trim().min(1).max(30)).min(2).max(8),
    rows: z
        .array(z.array(z.string().trim().max(30)))
        .min(1)
        .max(20),
    note: z.string().trim().max(500).default(""),
})
    .refine((g) => g.rows.every((r) => r.length === g.columns.length), {
    message: "Every row needs one cell per column",
    path: ["rows"],
});
export const productStorefrontSchema = z.object({
    productId: z.uuid(),
    published: z.boolean(),
    publishedAt: z.iso.datetime().nullable(),
    /** Null until first published. */
    slug: z.string().nullable(),
    /** Empty: the product name. */
    seoTitle: z.string(),
    /** Empty: the start of the description. */
    seoDescription: z.string(),
    /** ST-07: specifications, size chart and up to 8 products shown as "pairs well with". */
    specs: productSpecsSchema,
    sizeGuide: sizeGuideSchema.nullable(),
    relatedProductIds: z.array(z.uuid()).max(8),
    version: z.int(),
});
export const updateProductStorefrontSchema = z
    .object({
    published: z.boolean().optional(),
    slug: productSlugSchema.optional(),
    seoTitle: z.string().trim().max(70).optional(),
    seoDescription: z.string().trim().max(170).optional(),
    specs: productSpecsSchema.optional(),
    sizeGuide: sizeGuideSchema.nullable().optional(),
    relatedProductIds: z.array(z.uuid()).max(8).optional(),
    version: z.int().min(0),
})
    .strict();
export const getProductStorefront = defineEndpoint({
    ...tag,
    name: "getProductStorefront",
    method: "GET",
    path: "/v1/products/{id}/storefront",
    access: { permission: "catalog.view" },
    summary: "A product's online store settings",
    params: idParamsSchema,
    status: 200,
    response: productStorefrontSchema,
});
export const updateProductStorefront = defineEndpoint({
    ...tag,
    name: "updateProductStorefront",
    method: "PATCH",
    path: "/v1/products/{id}/storefront",
    access: manage,
    summary: "Publish or hide a product online, change its address and search texts",
    params: idParamsSchema,
    body: updateProductStorefrontSchema,
    status: 200,
    response: productStorefrontSchema,
    errors: ["CONFLICT", "SLUG_TAKEN", "INVALID_TRANSITION"],
});
export const bulkProductStorefrontSchema = z
    .object({ productIds: z.array(z.uuid()).min(1).max(200), published: z.boolean() })
    .strict();
export const bulkProductStorefrontResultSchema = z.object({
    /** Changed now. */
    changed: z.int(),
    /** Archived products are never published. An unknown product makes the whole request 404. */
    skipped: z.array(z.uuid()),
});
export const bulkProductStorefront = defineEndpoint({
    ...tag,
    name: "bulkProductStorefront",
    method: "POST",
    path: "/v1/products/storefront-bulk",
    access: manage,
    summary: "Publish or hide up to 200 products at once",
    body: bulkProductStorefrontSchema,
    status: 200,
    response: bulkProductStorefrontResultSchema,
});
/** `catalog.product.storefront_changed`: published, hidden, new slug or texts (search index, public images, cache). */
export const productStorefrontChangedEventSchema = z.object({
    productId: z.uuid(),
    published: z.boolean(),
    slug: z.string().nullable(),
    /** Product and variant images to serve publicly while published. */
    imageFileIds: z.array(z.uuid()),
});
//# sourceMappingURL=catalog-storefront.js.map